import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, ToggleButton, ToggleButtonGroup, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { ResponsiveLine } from '@nivo/line';
import { colorForSignal, formatValue, formatDateUTC } from './studioUtils';

/**
 * Colourful, interactive multi-line preview of the segment data.
 *
 * Each numeric signal becomes its own coloured line. Because raw magnitudes
 * differ wildly (a price next to a 0/1 flag), the chart defaults to a
 * "compare shapes" mode that min-max normalises every series to 0..1 - the
 * lines then overlay cleanly and the relationships pop. The tooltip always
 * reports the real, un-normalised value so nothing is misleading.
 */
const PreviewChart = ({ sanitized, colorMap }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [scaleMode, setScaleMode] = useState('normalized');

  const numericColumns = useMemo(
    () => (sanitized?.columns || []).filter((c) => c.isNumeric),
    [sanitized],
  );

  const series = useMemo(() => {
    if (!sanitized) return [];
    const ts = sanitized.timestamps;
    return numericColumns.map((col) => {
      let lo = Infinity;
      let hi = -Infinity;
      for (const v of col.values) {
        if (typeof v === 'number') {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
      const span = hi - lo;
      const data = ts.map((t, i) => {
        const raw = col.values[i];
        let y = raw;
        if (scaleMode === 'normalized') {
          y = span > 0 ? (raw - lo) / span : 0.5;
        }
        return { x: new Date(t), y, raw, filled: col.mask[i] };
      });
      return {
        id: col.label,
        key: col.key,
        color: colorMap[col.key] || colorForSignal(col.key),
        isLabel: col.isLabel,
        data,
      };
    });
  }, [sanitized, numericColumns, scaleMode, colorMap]);

  const axisText = dark ? '#9aa0a6' : '#5f6368';
  const gridColor = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  const nivoTheme = {
    background: 'transparent',
    textColor: axisText,
    fontSize: 11,
    axis: {
      domain: { line: { stroke: gridColor } },
      ticks: { line: { stroke: gridColor }, text: { fill: axisText } },
    },
    grid: { line: { stroke: gridColor, strokeDasharray: '3 4' } },
    crosshair: { line: { stroke: theme.palette.primary.main, strokeWidth: 1, strokeOpacity: 0.6 } },
    tooltip: { container: { background: 'transparent', boxShadow: 'none', padding: 0 } },
  };

  const hasData = series.length > 0 && series.some((s) => s.data.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, pb: 0.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShowChartIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Live preview
          </Typography>
          {sanitized && (
            <Typography variant="caption" color="text.secondary">
              {numericColumns.length} signal{numericColumns.length === 1 ? '' : 's'}
              {' · '}
              {sanitized.timestamps.length} points
            </Typography>
          )}
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scaleMode}
          onChange={(_e, v) => v && setScaleMode(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              py: 0.2,
              px: 1,
              fontSize: '0.72rem',
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <ToggleButton value="normalized">Compare shapes</ToggleButton>
          <ToggleButton value="raw">Raw values</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {hasData ? (
          <ResponsiveLine
            data={series}
            margin={{ top: 16, right: 24, bottom: 36, left: 48 }}
            xScale={{ type: 'time', format: 'native', precision: 'minute' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
            curve="monotoneX"
            colors={(s) => s.color}
            lineWidth={2}
            enablePoints={false}
            enableGridX={false}
            enableArea={scaleMode === 'normalized' && series.length === 1}
            areaOpacity={0.12}
            useMesh
            enableSlices="x"
            animate
            motionConfig="gentle"
            theme={nivoTheme}
            axisLeft={{
              tickSize: 0,
              tickPadding: 8,
              format: (v) =>
                scaleMode === 'normalized' ? '' : formatValue(v),
            }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              tickValues: 5,
              format: (v) => formatDateUTC(v),
            }}
            sliceTooltip={({ slice }) => {
              const x = slice.points[0]?.data?.x;
              return (
                <Box
                  sx={{
                    bgcolor: dark ? 'rgba(20,22,20,0.96)' : 'rgba(255,255,255,0.98)',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    p: 1,
                    boxShadow: 4,
                    minWidth: 180,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {x ? `${formatDateUTC(x, true)} UTC` : ''}
                  </Typography>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {slice.points.map((p) => (
                      <Stack
                        key={p.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1.5}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: p.serieColor,
                            }}
                          />
                          <Typography variant="caption">{p.serieId}</Typography>
                        </Stack>
                        <Typography variant="caption" fontWeight={700}>
                          {formatValue(p.data.raw)}
                          {p.data.filled && (
                            <Box
                              component="span"
                              sx={{ color: 'text.disabled', ml: 0.5, fontWeight: 400 }}
                            >
                              ~
                            </Box>
                          )}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              );
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Pick a signal to see it come to life.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Legend */}
      {hasData && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', gap: 0.5, px: 1, pt: 0.5, pb: 1.25 }}
          >
          {series.map((s) => (
            <Chip
              key={s.key}
              size="small"
              label={s.id}
              sx={{
                height: 20,
                fontSize: '0.68rem',
                bgcolor: 'transparent',
                border: `1px solid ${s.color}`,
                color: 'text.primary',
                '& .MuiChip-label': { px: 0.75 },
                '&::before': {
                  content: '""',
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: s.color,
                  marginLeft: '6px',
                },
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PreviewChart;
